import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMaxWidth } from '../../../hooks/media-query';

import { Target } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { PageLayout } from '../../../components/ui/PageLayout';
import GoalNode from './components/GoalNode';
import FilterPanel from './components/FilterPanel';
import GoalDetailModal from './components/GoalDetailModal';
import GoalListView from './components/GoalListView';
import MobileGoalTree from './components/MobileGoalTree';

import type { RootState } from '../../../store/redux/store/store';
import { useGoals } from './useGoals';

// ----------------------
// COMPONENT
// ----------------------

export default function GoalAlignmentMap() {
  const { t } = useTranslation();
  const userId = useSelector((state: RootState) => {
    const user = state.user as Record<string, unknown>;
    return (user?.userId as string)
      || (user?.id as string)
      || ((user?.profile as Record<string, unknown>)?.sub as string)
      || (((user?.userAuthInfo as Record<string, unknown>)?.profile as Record<string, unknown>)?.sub as string)
      || localStorage.getItem('currentUserId')
      || '';
  });
  const isMobile = useMaxWidth(1199);

  const {
    // State
    isFilterPanelOpen,
    setIsFilterPanelOpen,
    selectedGoal,
    isModalOpen,
    setIsModalOpen,
    modalMode,
    hoveredGoal,
    setHoveredGoal,
    expandedSections,
    viewType,
    filters,
    goals,
    expandedNodes,
    loading,
    stats,
    error,
    successMessage,
    departments,
    loadingDepartments,
    availableOwners,
    loadingOwners,
    visibleCounts,

    // Computed
    filteredGoals,

    // Functions
    getStatusLabel,
    buildTreePositionMap,
    toggleNodeExpanded,
    loadMoreGoals,

    // Handlers
    handleGoalClick,
    handleCreateNewGoal,
    handleSaveGoal,
    handleDeleteGoal,
    handleUpdateProgress,
    handleAddComment,
    handleAddMilestone,
    handleUpdateMilestone,
    handleCloneGoal,
    handleToggleArchive,
    handleFilterChange,
    handleToggleSection,
    handleViewToggle,
    handleCloseError,
    handleCloseSuccess,
  } = useGoals(userId, t);

  // Auto-dismiss toasts
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(handleCloseError, 6000);
      return () => clearTimeout(timer);
    }
  }, [error, handleCloseError]);

  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(handleCloseSuccess, 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, handleCloseSuccess]);

  // Memoize tree layout computation
  const { posMap, maxX, maxY } = React.useMemo(() => {
    const posMap = buildTreePositionMap();
    let maxX = 1100, maxY = 600;
    posMap.forEach(pos => {
      maxX = Math.max(maxX, pos.x + pos.width + 40);
      maxY = Math.max(maxY, pos.y + pos.height + 40);
    });
    return { posMap, maxX, maxY };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGoals, expandedNodes, filters]);

  if (isMobile) {
    return (
      <>
        <PageLayout
          title={t('Goal Alignment Map')}
          description={t('Align and track your individual, team, and company objectives')}
          icon={Target}
          accentColor="rose"
          maxWidth="1600px"
          headerActions={
            <div className="flex gap-1 w-full">
              <Button
                variant="outline"
                size="icon"
                onClick={handleViewToggle}
                iconName={viewType === 'map' ? 'List' : 'Map'}
                aria-label={viewType === 'map' ? t('List View') : t('Map View')}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCreateNewGoal}
                iconName="Plus"
                aria-label={t('New Goal')}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                iconName="ListFilter"
                aria-label={t('Filters')}
                className={isFilterPanelOpen ? 'text-primary border-primary' : ''}
              />
            </div>
          }
        >
          {loading ? (
              <div className="flex justify-center p-4">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              </div>
            ) : viewType === 'list' ? (
              <GoalListView
                filteredGoals={filteredGoals}
                visibleCounts={visibleCounts}
                loading={loading}
                filters={filters}
                handleGoalClick={handleGoalClick}
                loadMoreGoals={loadMoreGoals}
                getStatusLabel={getStatusLabel}
                t={t}
              />
            ) : (
              <MobileGoalTree
                goals={filteredGoals}
                onGoalClick={handleGoalClick}
                expandedSections={expandedSections}
                onToggleSection={handleToggleSection}
              />
            )}

            {/* Filter Panel for mobile */}
            {isFilterPanelOpen && (
              <div className="fixed inset-0 z-[1300] flex justify-end bg-black/50">
                <div className="w-[85%] max-w-[400px] h-full bg-card overflow-auto">
                  <FilterPanel
                    isOpen={isFilterPanelOpen}
                    onToggle={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    isMobile={true}
                    allGoals={goals}
                    departments={departments}
                    loadingDepartments={loadingDepartments}
                  />
                </div>
              </div>
            )}
        </PageLayout>

        {/* Goal Detail Modal */}
        <GoalDetailModal
          goal={selectedGoal}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSaveGoal={handleSaveGoal}
          onDeleteGoal={handleDeleteGoal}
          onUpdateProgress={handleUpdateProgress}
          onAddComment={handleAddComment}
          onAddMilestone={handleAddMilestone}
          onUpdateMilestone={handleUpdateMilestone}
          onCloneGoal={handleCloneGoal}
          onToggleArchive={handleToggleArchive}
          allGoals={goals}
          mode={modalMode}
          loading={loading}
          departments={departments}
          loadingDepartments={loadingDepartments}
          availableOwners={availableOwners}
          loadingOwners={loadingOwners}
        />

        {/* Toast Notifications */}
        {error && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1400] max-w-md w-full px-4">
            <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 shadow-lg">
              <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
              <span className="flex-1 text-sm">{error}</span>
              <button onClick={handleCloseError} className="p-1 hover:bg-destructive/10 rounded flex-shrink-0">
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        )}
        {successMessage && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1400] max-w-md w-full px-4">
            <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success rounded-lg p-3 shadow-lg">
              <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
              <span className="flex-1 text-sm">{successMessage}</span>
              <button onClick={handleCloseSuccess} className="p-1 hover:bg-success/10 rounded flex-shrink-0">
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <PageLayout
        title={t('Goal Alignment Map')}
        description={t('Align and track your individual, team, and company objectives')}
        icon={Target}
        accentColor="rose"
        maxWidth="1600px"
        headerActions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleViewToggle}
              data-testid="view-toggle-btn"
            >
              {viewType === 'map' ? <Icon name="List" size={16} /> : <Icon name="Map" size={16} />}
              <span className="ms-2">{viewType === 'map' ? t('List View') : t('Map View')}</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleCreateNewGoal}
              disabled={loading}
              data-testid="new-goal-btn"
            >
              <Icon name="Plus" size={16} />
              <span className="ms-2">{t('New Goal')}</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              iconName="ListFilter"
              aria-label={t('Filters')}
              className={isFilterPanelOpen ? 'text-primary border-primary' : ''}
            />
          </div>
        }
      >
        {/* Main Content */}
          <div className="flex gap-3 min-w-0">
            {/* Filter Panel */}
            <div className={`w-[280px] flex-shrink-0 ${isFilterPanelOpen ? 'block' : 'hidden'}`}>
              <FilterPanel
                isOpen={isFilterPanelOpen}
                onToggle={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                filters={filters}
                onFilterChange={handleFilterChange}
                isMobile={false}
                allGoals={goals}
                departments={departments}
                loadingDepartments={loadingDepartments}
              />
            </div>

            {/* Conditional View Rendering */}
            <div className="flex-1 min-h-[70vh] min-w-0">
              {loading ? (
                <div className="flex justify-center items-center h-[50vh]">
                  <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                </div>
              ) : viewType === 'list' ? (
                <GoalListView
                filteredGoals={filteredGoals}
                visibleCounts={visibleCounts}
                loading={loading}
                filters={filters}
                handleGoalClick={handleGoalClick}
                loadMoreGoals={loadMoreGoals}
                getStatusLabel={getStatusLabel}
                t={t}
              />
              ) : (
                <div
                  className="relative h-full min-w-0 max-w-full overflow-auto bg-background rounded-lg p-3"
                  data-testid="goal-alignment-map"
                >
                  <div className="relative" style={{ minWidth: maxX, minHeight: maxY }}>
                        {/* Connection Lines */}
                        <svg style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, width: '100%', height: '100%' }}>
                          <defs>
                            <marker id="arrowhead-default" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                              <polygon points="0 0, 10 3.5, 0 7" fill="var(--color-border)" />
                            </marker>
                            <marker id="arrowhead-active" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                              <polygon points="0 0, 10 3.5, 0 7" fill="var(--color-primary)" />
                            </marker>
                          </defs>

                          {filteredGoals.map((goal) => {
                            if (!goal.parentId) return null;
                            const parentPos = posMap.get(goal.parentId);
                            const childPos = posMap.get(goal.id);
                            if (!parentPos || !childPos) return null;
                            if (!expandedNodes.has(goal.parentId)) return null;

                            const startX = parentPos.x + parentPos.width / 2;
                            const startY = parentPos.y + parentPos.height;
                            const endX = childPos.x + childPos.width / 2;
                            const endY = childPos.y;
                            const midY = startY + (endY - startY) / 2;
                            const isActive = hoveredGoal === goal.id || hoveredGoal === goal.parentId;

                            return (
                              <path
                                key={`conn-${goal.parentId}-${goal.id}`}
                                d={`M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`}
                                fill="none"
                                stroke={isActive ? 'var(--color-primary)' : 'var(--color-border)'}
                                strokeWidth={isActive ? 2.5 : 1.5}
                                strokeOpacity={isActive ? 0.9 : 0.5}
                                strokeDasharray={isActive ? 'none' : '4 2'}
                                markerEnd={`url(#arrowhead-${isActive ? 'active' : 'default'})`}
                              />
                            );
                          })}
                        </svg>

                        {/* Goal Nodes with expand/collapse */}
                        {filteredGoals.map((goal) => {
                          const position = posMap.get(goal.id);
                          if (!position) return null;
                          const hasChildren = filteredGoals.some(g => g.parentId === goal.id);
                          const isExpanded = expandedNodes.has(goal.id);

                          return (
                            <div
                              key={goal.id}
                              data-testid={`goal-node-${goal.id}`}
                              className="absolute"
                              style={{
                                left: position.x,
                                top: position.y,
                                width: position.width,
                              }}
                              onMouseEnter={() => setHoveredGoal(goal.id)}
                              onMouseLeave={() => setHoveredGoal(null)}
                            >
                              <div className="relative">
                                <GoalNode
                                  goal={goal}
                                  level={goal.level}
                                  onNodeClick={() => handleGoalClick(goal)}
                                  isConnected={hoveredGoal === goal.id ||
                                    filteredGoals.some((g) => g.parentId === goal.id && hoveredGoal === g.id)}
                                />
                                {/* Expand/collapse toggle */}
                                {hasChildren && (
                                  <div
                                    data-testid={`goal-expand-${goal.id}`}
                                    onClick={(e) => { e.stopPropagation(); toggleNodeExpanded(goal.id); }}
                                    className={`absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-card border-2 flex items-center justify-center cursor-pointer z-10 transition-all duration-200 hover:bg-primary/10 hover:border-primary ${
                                      isExpanded ? 'border-primary' : 'border-border'
                                    }`}
                                  >
                                    <Icon
                                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                                      size={14}
                                      color={isExpanded ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                                    />
                                  </div>
                                )}
                                {/* Owner badge */}
                                {goal.owner && goal.level !== 'company' && (
                                  <span
                                    className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[0.65rem] text-muted-foreground bg-card px-1 rounded"
                                    style={{
                                      bottom: hasChildren ? -28 : -16,
                                    }}
                                  >
                                    {goal.owner}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {/* Summary Stats */}
                        <div
                          data-testid="goal-summary-stats"
                          className="absolute top-4 end-4 bg-card rounded-lg shadow-sm p-4 min-w-[200px] z-[5]"
                        >
                          <h6 className="text-sm font-medium mb-2">
                            {t('Goal Summary')}
                          </h6>
                          <div className="grid gap-1">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{t('Total Goals')}:</span>
                              <span className="text-sm font-medium">{stats?.totalGoals || filteredGoals.length}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{t('On Track')}:</span>
                              <span className="text-sm font-medium text-primary">
                                {stats?.onTrack || stats?.onTrackGoals || filteredGoals.filter(g => g.status === 'on-track').length}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{t('At Risk')}:</span>
                              <span className="text-sm font-medium text-warning">
                                {stats?.atRisk || stats?.atRiskGoals || filteredGoals.filter(g => g.status === 'at-risk').length}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{t('Completed')}:</span>
                              <span className="text-sm font-medium text-success">
                                {stats?.completed || stats?.completedGoals || filteredGoals.filter(g => g.status === 'completed').length}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{t('Avg Progress')}:</span>
                              <span className="text-sm font-medium">
                                {stats?.averageProgress ? `${Number(stats.averageProgress).toFixed(1)}%` :
                                  filteredGoals.length > 0 ?
                                    `${(filteredGoals.reduce((sum, g) => sum + g.progress, 0) / filteredGoals.length).toFixed(1)}%` :
                                    'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                </div>
              )}
            </div>
          </div>
      </PageLayout>

      {/* Goal Detail Modal */}
      <GoalDetailModal
        goal={selectedGoal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaveGoal={handleSaveGoal}
        onDeleteGoal={handleDeleteGoal}
        onUpdateProgress={handleUpdateProgress}
        onAddComment={handleAddComment}
        onAddMilestone={handleAddMilestone}
        onUpdateMilestone={handleUpdateMilestone}
        onCloneGoal={handleCloneGoal}
        onToggleArchive={handleToggleArchive}
        allGoals={goals}
        mode={modalMode}
        loading={loading}
        departments={departments}
        loadingDepartments={loadingDepartments}
        availableOwners={availableOwners}
        loadingOwners={loadingOwners}
      />

      {/* Toast Notifications */}
      {error && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1400] max-w-md w-full px-4">
          <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 shadow-lg">
            <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
            <span className="flex-1 text-sm">{error}</span>
            <button onClick={handleCloseError} className="p-1 hover:bg-destructive/10 rounded flex-shrink-0">
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1400] max-w-md w-full px-4">
          <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success rounded-lg p-3 shadow-lg">
            <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
            <span className="flex-1 text-sm">{successMessage}</span>
            <button onClick={handleCloseSuccess} className="p-1 hover:bg-success/10 rounded flex-shrink-0">
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
